import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, ChevronLeft } from "lucide-react";

const steps = [
  { id: 1, label: "Dati del gruppo" },
  { id: 2, label: "Invio richiesta" },
];

export default function Prenota() {
  const [step, setStep] = useState(1);
  const [availabilities, setAvailabilities] = useState({}); // key `${lab_id}-${date}` -> {available, capacity}
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [form, setForm] = useState({
    contact_first_name: "", contact_last_name: "", contact_email: "",
    contact_phone: "", organization: "",
    group_type: "scuola", num_participants: 10,
    check_in: "", check_out: "", accommodation_needed: true,
    selected_labs: [],
    guests: [],
    notes: "",
  });

  const datesInvalid = form.check_in && form.check_out && form.check_out <= form.check_in;

  const canNext = () => {
    if (step === 1) return form.contact_first_name && form.contact_last_name && form.contact_email && form.check_in && form.check_out && form.num_participants > 0
      && !datesInvalid;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", form);
      setConfirmed(data);
      toast.success("Richiesta inviata!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito: riprova tra qualche istante o contattaci a info@cortedellaminiera.it");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <Layout>
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-24 text-center" data-testid="booking-confirmation">
          <CheckCircle2 size={56} className="mx-auto text-[#8C3A27]" />
          <div className="overline mt-6">Richiesta ricevuta</div>
          <h1 className="font-display text-5xl mt-3">Grazie, {confirmed.contact_first_name}!</h1>
          <p className="mt-6 text-[#6B6960] max-w-xl mx-auto">
            Abbiamo inviato una email di conferma a <strong>{confirmed.contact_email}</strong>.
            Il nostro staff la ricontatterà a breve per confermare disponibilità e dettagli.
          </p>
          <div className="mt-8 inline-block border border-[#E2DFD6] bg-white px-8 py-6">
            <div className="overline">Riferimento</div>
            <div className="font-display text-3xl mt-1">{confirmed.reference}</div>
          </div>
          <div className="mt-10">
            <Button onClick={() => window.location.assign("/")} className="btn-primary-cdm rounded-full h-11 px-7">Torna alla home</Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="overline">Prenotazione gruppi</div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] mt-3">Componi il tuo soggiorno.</h1>

        {/* Steps */}
        <div className="mt-10 grid grid-cols-2 gap-2">
          {steps.map((s) => (
            <div key={s.id} className={`p-3 border-t-2 ${step >= s.id ? "border-[#8C3A27]" : "border-[#E2DFD6]"}`}>
              <div className="overline">{`0${s.id}`}</div>
              <div className={`text-sm mt-1 ${step >= s.id ? "text-[#1A1A1A]" : "text-[#6B6960]"}`}>{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* MAIN */}
          <div className="lg:col-span-8 bg-white border border-[#E2DFD6] p-8">
            {step === 1 && (
              <div className="space-y-4" data-testid="step-group">
                <h2 className="font-display text-3xl">Info gruppo & referente</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Nome referente</Label><Input data-testid="input-first-name" value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} /></div>
                  <div><Label>Cognome referente</Label><Input data-testid="input-last-name" value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="input-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                  <div><Label>Telefono</Label><Input data-testid="input-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
                  <div><Label>Organizzazione / Scuola</Label><Input data-testid="input-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></div>
                  <div><Label>Tipo gruppo</Label>
                    <Select value={form.group_type} onValueChange={(v) => setForm({ ...form, group_type: v })}>
                      <SelectTrigger data-testid="select-group-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scuola">Scuola / PCTO</SelectItem>
                        <SelectItem value="erasmus">Erasmus VET</SelectItem>
                        <SelectItem value="famiglia">Famiglia estesa</SelectItem>
                        <SelectItem value="azienda">Azienda / Team</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Partecipanti</Label><Input data-testid="input-participants" type="number" min="1" value={form.num_participants} onChange={(e) => setForm({ ...form, num_participants: parseInt(e.target.value || "0") })} /></div>
                  <div><Label translate="no">Data di arrivo (check-in)</Label><Input data-testid="input-checkin" type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} /></div>
                  <div><Label translate="no">Data di partenza (check-out)</Label><Input data-testid="input-checkout" type="date" min={form.check_in || undefined} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className={datesInvalid ? "border-red-600" : ""} />
                    {datesInvalid && <p className="text-xs text-red-700 mt-1" data-testid="dates-error">La data di partenza deve essere successiva a quella di arrivo.</p>}
                  </div>
                </div>

              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="step-review">
                <h2 className="font-display text-3xl">Rivedi e invia</h2>
                <div><Label>Note (opzionale)</Label><Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="text-sm text-[#6B6960] leading-relaxed">
                  Cliccando "Invia richiesta" acconsenti al trattamento dei dati per l'esecuzione della prenotazione
                  e a essere ricontattato dalla segreteria. Dopo la conferma della disponibilità riceverai il modulo per i dati degli ospiti (art. 109 TULPS).
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} className="rounded-full h-11 px-6 border-[#1A1A1A]/20" data-testid="btn-prev">
                <ChevronLeft size={16} className="mr-1" /> Indietro
              </Button>
              {step < 2 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="btn-primary-cdm rounded-full h-11 px-6" data-testid="btn-next">
                  Avanti <ChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={submitting} className="btn-primary-cdm rounded-full h-11 px-7" data-testid="btn-submit-booking">
                  {submitting ? "Invio…" : "Invia richiesta"}
                </Button>
              )}
            </div>
          </div>

          {/* SUMMARY */}
          <aside className="lg:col-span-4">
            <div className="border border-[#E2DFD6] bg-white p-6 sticky top-24">
              <div className="overline">Riepilogo</div>
              <div className="font-display text-2xl mt-2">Il tuo soggiorno</div>
              <div className="mt-4 space-y-2 text-sm text-[#1A1A1A]">
                <div>Referente: {form.contact_first_name} {form.contact_last_name || "—"}</div>
                <div>Partecipanti: {form.num_participants}</div>
                <div translate="no">Check-in: {form.check_in || "—"}</div>
                <div translate="no">Check-out: {form.check_out || "—"}</div>
              </div>
              <div className="mt-4 divider-line" />
              <p className="text-xs text-[#6B6960] mt-4">Nessun preventivo automatico: la struttura vi ricontatterà per confermare disponibilità, dettagli e condizioni.</p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
